import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import { tenantService } from '../services/tenantService';
import { propertyService } from '../services/propertyService';

const TenantDocumentsPage = () => {
  const { currentUser, userData } = useAuth();
  const [tenantAssignments, setTenantAssignments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser && userData) {
      const unsubscribeFunctions = [];

      // Subscribe to tenant assignments
      const assignmentUnsubscribe = tenantService.subscribeToTenantAssignmentsByTenant(
        currentUser.uid,
        (assignments) => {
          setTenantAssignments(assignments || []);
        }
      );
      unsubscribeFunctions.push(assignmentUnsubscribe);

      // Subscribe to properties
      const propertyUnsubscribe = propertyService.subscribeToPropertiesByTenant(
        currentUser.uid,
        (properties) => {
          setProperties(properties || []);
          setLoading(false);
        }
      );
      unsubscribeFunctions.push(propertyUnsubscribe);

      return () => {
        unsubscribeFunctions.forEach(unsubscribe => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      };
    } else {
      setLoading(false);
    }
  }, [currentUser, userData]);

  const handleDownloadDocument = (documentType) => {
    alert(`Downloading ${documentType}...`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  const currentAssignment = tenantAssignments[0];
  const currentProperty = properties[0];

  // Only show documents if tenant has a property assignment
  const documents = currentAssignment ? [
    {
      id: 1,
      name: 'Lease Agreement',
      description: 'Your signed lease agreement',
      date: currentAssignment.leaseStartDate,
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
    },
    {
      id: 2,
      name: 'Property Information',
      description: 'Property details and amenities',
      date: new Date(),
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
    }
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-64">
          <header className="bg-white shadow-sm border-b border-gray-200">
            <div className="px-8 py-6">
              <h1 className="text-2xl font-bold text-gray-800">Documents</h1>
            </div>
          </header>

          <div className="p-8">
            <div className="max-w-4xl mx-auto space-y-6">
              {!currentAssignment ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-800 mb-2">No documents available</h3>
                  <p className="text-gray-600">You need to be assigned to a property to access documents.</p>
                </div>
              ) : (
                <>
                  {/* Documents List */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Available Documents</h2>
                    
                    <div className="space-y-4">
                      {documents.map((document) => (
                        <div key={document.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={document.icon} />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-sm font-medium text-gray-800">{document.name}</h3>
                              <p className="text-xs text-gray-500">{document.description}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(document.date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownloadDocument(document.name)}
                            className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 hover:border-blue-300"
                          >
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Property Information */}
                  {currentProperty && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h2 className="text-xl font-semibold text-gray-800 mb-4">Property Information</h2>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-lg font-medium text-gray-800 mb-2">{currentProperty.name}</h3>
                          <p className="text-gray-600 mb-4">{currentProperty.address}</p>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Property Type:</span>
                              <span className="text-sm font-medium text-gray-800 capitalize">{currentProperty.type}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Monthly Rent:</span>
                              <span className="text-sm font-medium text-gray-800">${currentAssignment.monthlyRent?.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Lease Start:</span>
                              <span className="text-sm font-medium text-gray-800">
                                {new Date(currentAssignment.leaseStartDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Lease End:</span>
                              <span className="text-sm font-medium text-gray-800">
                                {new Date(currentAssignment.leaseEndDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantDocumentsPage; 